import React, { useEffect, useState, useCallback } from "react";
import { Plus, ArrowUpCircle } from "lucide-react";
import api from "@/lib/api";
import { fmtEUR, MONTHS_FR } from "@/lib/format";
import { CARD, Label, Loader, EmptyState, ProgressBar, ErrorRetry } from "@/components/common";
import { TxRow } from "@/pages/Mouvements";
import TransactionDialog from "@/components/TransactionDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const now = new Date();

export default function Depenses() {
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [txns, setTxns] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [add, setAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [t, d] = await Promise.all([
        api.get(`/transactions?year=${ym.year}&month=${ym.month}&type=expense`),
        api.get(`/dashboard?year=${ym.year}&month=${ym.month}`),
      ]);
      setTxns(t.data); setDash(d.data);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, [ym]);

  useEffect(() => { load(); }, [load]);
  const c = dash?.current;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Ce qui sort</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Dépenses</h1>
        </div>
        <Button data-testid="add-expense-btn" onClick={() => setAdd(true)} className="rounded-full bg-[#065F46] hover:bg-[#047857]"><Plus className="mr-1.5 h-4 w-4" />Dépense</Button>
      </div>

      <div className="mt-4">
        <Select value={`${ym.year}-${ym.month}`} onValueChange={(v) => { const [y, m] = v.split("-"); setYm({ year: +y, month: +m }); }}>
          <SelectTrigger className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Array.from({ length: 15 }).map((_, i) => {
              const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1);
              return <SelectItem key={i} value={`${d.getFullYear()}-${d.getMonth() + 1}`}>{MONTHS_FR[d.getMonth() + 1]} {d.getFullYear()}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {loading ? <Loader /> : error || !c ? <ErrorRetry onRetry={load} /> : (
        <div className="mt-5 space-y-5">
          <div className={`${CARD} p-6`}>
            <div className="flex items-center justify-between">
              <Label>Dépenses payées</Label>
              <span className="font-display text-xl font-bold text-amber-600">{c.progression_depenses}%</span>
            </div>
            <div className="mt-3"><ProgressBar value={c.progression_depenses} color="amber" height="h-4" /></div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
              <div><p className="text-xs text-gray-400">Déjà payé</p><p className="font-bold">{fmtEUR(c.deja_paye)}</p></div>
              <div><p className="text-xs text-gray-400">Prévu total</p><p className="font-bold">{fmtEUR(c.depenses_prevues)}</p></div>
              <div><p className="text-xs text-gray-400">Reste à payer</p><p className="font-bold text-rose-600">{fmtEUR(c.reste_a_payer)}</p></div>
            </div>
          </div>

          {dash.expense_breakdown?.length > 0 && (
            <div className={`${CARD} p-6`}>
              <Label className="mb-3">Répartition par poste</Label>
              <div className="space-y-2.5">
                {dash.expense_breakdown.map((g, i) => {
                  const max = dash.expense_breakdown[0]?.amount || 1;
                  return (
                    <div key={i}>
                      <div className="flex justify-between text-sm"><span className="font-medium">{g.name}</span><span className="font-semibold">{fmtEUR(g.amount)}</span></div>
                      <div className="mt-1"><ProgressBar value={(g.amount / max) * 100} color="rose" height="h-2" /></div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className={`${CARD} p-4 sm:p-6`}>
            <Label className="mb-2">Dépenses du mois</Label>
            {txns.length === 0 ? <EmptyState title="Aucune dépense" subtitle="Ajoutez votre première dépense (récurrente ou ponctuelle)." action={<Button onClick={() => setAdd(true)} className="rounded-full bg-[#065F46]">Ajouter</Button>} />
              : txns.map((t) => <TxRow key={t.id} t={t} onChange={load} />)}
          </div>
        </div>
      )}

      <TransactionDialog open={add} onOpenChange={setAdd} type="expense" onSaved={load} />
    </div>
  );
}
